import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Copy, ExternalLink, CheckCircle2, AlertCircle, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS_BADGE = {
  monetized: ["Your link", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  generic: ["Not earning", "bg-amber-50 text-amber-700 border-amber-200"],
  missing: ["No URL", "bg-rose-50 text-rose-700 border-rose-200"],
};

export const AdminLinkManager = ({ client, API }) => {
  const [data, setData] = useState(null);
  const [generating, setGenerating] = useState(null);
  const [pack, setPack] = useState(null);
  const [filter, setFilter] = useState("all");
  const [bulk, setBulk] = useState(null);

  const load = useCallback(() => {
    client().get(`${API}/admin/link-audit`).then((r) => setData(r.data)).catch(() => {});
  }, [client, API]);

  useEffect(() => {
    load();
    client().get(`${API}/admin/application-packs/bulk-status`).then((r) => { if (r.data.running) setBulk(r.data); }).catch(() => {});
  }, [load, client, API]);

  useEffect(() => {
    if (!bulk?.running) return;
    const t = setInterval(() => {
      client().get(`${API}/admin/application-packs/bulk-status`).then((r) => {
        setBulk(r.data);
        if (!r.data.running) { load(); toast.success(`Bulk generation done — ${r.data.done - r.data.errors} packs created`); }
      }).catch(() => {});
    }, 4000);
    return () => clearInterval(t);
  }, [bulk?.running, client, API, load]);

  const startBulk = async () => {
    try {
      await client().post(`${API}/admin/application-packs/generate-all`);
      setBulk({ running: true, done: 0, total: 0, errors: 0 });
      toast.success("Bulk generation started — this runs in the background");
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Could not start");
    }
  };

  const generate = async (p, force = false) => {
    setGenerating(p.slug);
    try {
      const r = await client().post(`${API}/admin/programs/${p.slug}/application-pack${force ? "?force=true" : ""}`);
      setPack({ program: p, ...r.data.pack });
      if (!r.data.cached) load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Generation failed");
    } finally {
      setGenerating(null);
    }
  };

  const copy = (v) => navigator.clipboard.writeText(v).then(() => toast.success("Copied"));

  if (!data) return <p className="text-sm text-slate-400 py-6">Loading link audit…</p>;

  const rows = data.programs.filter((p) => filter === "all" || p.link_status === filter);

  return (
    <div data-testid="admin-link-manager">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <p className="text-sm text-slate-500 max-w-2xl">
          Replace each program's Join URL with <strong>your own affiliate link</strong> (edit it in the Programs tab) so every visitor click earns you commission.
          The AI assistant writes your application answers as <strong>{data.applicant.full_name}</strong> of {data.applicant.company}.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={startBulk} disabled={bulk?.running} data-testid="bulk-generate-button"
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold hover:bg-slate-700 transition-colors duration-200 disabled:opacity-60">
            {bulk?.running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {bulk?.running ? `Generating ${bulk.done}/${bulk.total || "…"}` : "Generate all missing"}
          </button>
          {[["all", `All (${data.programs.length})`], ["generic", `Not earning (${data.counts.generic})`], ["monetized", `Your links (${data.counts.monetized})`]].map(([k, label]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`link-filter-${k}`}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors duration-200 ${filter === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto bg-white border border-slate-200 rounded-lg">
        <table className="w-full text-sm min-w-[760px]">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-200">
            <th className="p-4">Program</th><th className="p-4">Niche</th><th className="p-4">Status</th><th className="p-4">Current join URL</th><th className="p-4 text-right">AI Assistant</th>
          </tr></thead>
          <tbody>
            {rows.map((p) => {
              const [label, cls] = STATUS_BADGE[p.link_status];
              return (
                <tr key={p.slug} data-testid={`link-audit-row-${p.slug}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors duration-150">
                  <td className="p-4 font-medium">{p.name}</td>
                  <td className="p-4 text-slate-500">{p.niche_name}</td>
                  <td className="p-4">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2.5 py-0.5 rounded-full border ${cls}`}>
                      {p.link_status === "monetized" ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />} {label}
                    </span>
                  </td>
                  <td className="p-4 max-w-[220px]"><p className="truncate font-mono text-xs text-slate-500">{p.join_url || "—"}</p></td>
                  <td className="p-4">
                    <div className="flex justify-end">
                      <button onClick={() => generate(p)} disabled={generating === p.slug} data-testid={`generate-pack-${p.slug}`}
                        className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-500 transition-colors duration-200 disabled:opacity-60">
                        {generating === p.slug ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                        {p.has_pack ? "View answers" : "AI answers"}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!pack} onOpenChange={(o) => !o && setPack(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="application-pack-dialog">
          {pack && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2"><Sparkles className="w-5 h-5 text-emerald-600" /> {pack.program.name} — application pack</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm space-y-2">
                  <p><span className="font-semibold">Network:</span> {pack.network_guess}</p>
                  {pack.signup_url && (
                    <a href={pack.signup_url} target="_blank" rel="noopener noreferrer" data-testid="pack-signup-url"
                      className="inline-flex items-center gap-1.5 text-emerald-700 font-medium hover:underline">
                      Open application page <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
                <div className="space-y-3">
                  {(pack.answers || []).map((a, i) => (
                    <div key={`${a.field}-${i}`} data-testid={`pack-answer-${i}`} className="border border-slate-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-xs uppercase tracking-wider font-semibold text-slate-500">{a.field}</p>
                        <button onClick={() => copy(a.value)} data-testid={`pack-copy-${i}`} aria-label={`Copy ${a.field}`}
                          className="p-1.5 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-900 transition-colors duration-200"><Copy className="w-3.5 h-3.5" /></button>
                      </div>
                      <p className="text-sm leading-relaxed">{a.value}</p>
                    </div>
                  ))}
                </div>
                {pack.tips?.length > 0 && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                    <p className="text-xs uppercase tracking-wider font-semibold text-emerald-700 mb-2">Approval tips</p>
                    <ul className="text-sm text-emerald-900 space-y-1.5 list-disc pl-4">
                      {pack.tips.map((t) => <li key={t}>{t}</li>)}
                    </ul>
                  </div>
                )}
                <button onClick={() => generate(pack.program, true)} disabled={!!generating} data-testid="pack-regenerate"
                  className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors duration-200">
                  {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Regenerate answers
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
